export type ComposerSubmitKey = {
  readonly key: string;
  readonly shiftKey: boolean;
  readonly isComposing: boolean;
  readonly keyCode: number;
};

export function shouldSubmitComposerOnEnter(event: ComposerSubmitKey): boolean {
  if (event.key !== 'Enter' || event.shiftKey) {
    return false;
  }
  if (event.isComposing || event.keyCode === 229) {
    return false;
  }
  return true;
}

export type ComposerCancelKey = {
  readonly key: string;
};

export function shouldCancelTurnOnEscape(event: ComposerCancelKey): boolean {
  return event.key === 'Escape';
}

export type ComposerHistoryKey = {
  readonly key: string;
  readonly shiftKey: boolean;
  readonly isComposing: boolean;
  readonly keyCode: number;
  readonly draft: string;
  readonly recallingHistory: boolean;
  readonly agentBusy: boolean;
};

export function shouldRecallOlderUserMessage(input: ComposerHistoryKey): boolean {
  if (input.agentBusy || input.key !== 'ArrowUp' || input.shiftKey) {
    return false;
  }
  if (input.isComposing || input.keyCode === 229) {
    return false;
  }
  return input.draft.trim() === '' || input.recallingHistory;
}

export function shouldRecallNewerUserMessage(input: ComposerHistoryKey): boolean {
  if (input.agentBusy || input.key !== 'ArrowDown' || input.shiftKey) {
    return false;
  }
  if (input.isComposing || input.keyCode === 229) {
    return false;
  }
  return input.recallingHistory;
}

export type ChatMessageTextSource = {
  readonly role: string;
  readonly text: string;
};

export function userMessageTexts(messages: readonly ChatMessageTextSource[]): readonly string[] {
  return messages
    .filter((message) => message.role === 'user' && message.text.trim() !== '')
    .map((message) => message.text);
}

export type UserMessageHistoryStepInput = {
  readonly texts: readonly string[];
  readonly historyIndex: number | undefined;
  readonly direction: 'older' | 'newer';
};

export type UserMessageHistoryStep = {
  readonly historyIndex: number | undefined;
  readonly draft: string;
};

export function stepUserMessageHistory(
  input: UserMessageHistoryStepInput
): UserMessageHistoryStep | undefined {
  const { texts, historyIndex, direction } = input;
  if (texts.length === 0) {
    return undefined;
  }
  if (direction === 'older') {
    const nextIndex = historyIndex === undefined ? 0 : Math.min(historyIndex + 1, texts.length - 1);
    return {
      historyIndex: nextIndex,
      draft: texts[texts.length - 1 - nextIndex] ?? '',
    };
  }
  if (historyIndex === undefined) {
    return undefined;
  }
  if (historyIndex === 0) {
    return { historyIndex: undefined, draft: '' };
  }
  const nextIndex = historyIndex - 1;
  return {
    historyIndex: nextIndex,
    draft: texts[texts.length - 1 - nextIndex] ?? '',
  };
}

export function composerHasContent(
  draft: string,
  attachmentCount: number,
  selectionCount = 0
): boolean {
  return draft.trim() !== '' || attachmentCount > 0 || selectionCount > 0;
}
